import { useLoaderData, useSearchParams } from '@remix-run/react';
import { useEffect, useMemo, useState } from 'react';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import type { UIComponent } from '~/services/layout';
import { getClientFeedUrl } from '~/services/request';
import { isValidUrl } from '~/utils/validations';
import useCurrentLanguage from './use-current-language';
import { getComputedUrl } from '~/services/pipes/utils';
import { useTabIndex } from '~/contexts/tab-index-context';
import {
  CONTEXT_SETTER_READY_EVENT,
  getStoredContextSetterKeys,
} from '~/utils/context-setter-storage.ts';

export type ClientFeedApiEndpointResult = {
  clientFeedApiEndpoint: string | null;
  hasClientContextInRequest: boolean;
  feedRequiresClientContext: boolean;
};

export function useClientFeedApiEndpoint({
  uiComponent,
  applyClientContext = true,
}: {
  uiComponent: UIComponent;
  applyClientContext?: boolean;
}): ClientFeedApiEndpointResult {
  const {
    serverLoadedFeeds,
    baseURL,
    isTabsScreen,
    tabs,
    screenEntry,
    contextSetterRemoteUrl,
  } = useLoaderData<typeof rootLoader>();

  const currentLanguage: string = useCurrentLanguage();
  const tabIndex = useTabIndex();
  const [searchParams] = useSearchParams();
  const [clientContextVersion, setClientContextVersion] = useState<number>(0);

  const q: string | null = searchParams.get('q');
  const filterTag: string | null = searchParams.get('filterTag');

  useEffect(() => {
    const handleContextSetterReady = (): void => {
      setClientContextVersion((version) => version + 1);
    };

    window.addEventListener(
      CONTEXT_SETTER_READY_EVENT,
      handleContextSetterReady
    );

    return () => {
      window.removeEventListener(
        CONTEXT_SETTER_READY_EVENT,
        handleContextSetterReady
      );
    };
  }, []);

  const entry =
    isTabsScreen && tabs?.entry && tabIndex !== undefined
      ? tabs.entry[tabIndex]
      : screenEntry || serverLoadedFeeds?.[0]?.feed?.entry?.[0];

  const feedUrl: string | undefined = getComputedUrl({
    component: uiComponent,
    entry,
    q,
    filterTag,
  });

  const storedClientContextKeys = useMemo(() => {
    void clientContextVersion;

    return getStoredContextSetterKeys();
  }, [clientContextVersion]);

  if (!isValidUrl(feedUrl)) {
    return {
      clientFeedApiEndpoint: null,
      hasClientContextInRequest: false,
      feedRequiresClientContext: false,
    };
  }

  const hasClientContextInRequest: boolean =
    applyClientContext && Object.keys(storedClientContextKeys).length > 0;

  const feedRequiresClientContextValue: boolean =
    applyClientContext && Boolean(contextSetterRemoteUrl);

  const clientFeedApiEndpoint: string = addQueryParam({
    clientFeedApiEndpoint: `${baseURL}/${getClientFeedUrl(
      feedUrl,
      hasClientContextInRequest ? storedClientContextKeys : undefined
    )}`,
    key: 'language',
    value: currentLanguage,
  });

  return {
    clientFeedApiEndpoint: clientFeedApiEndpoint || null,
    hasClientContextInRequest,
    feedRequiresClientContext: feedRequiresClientContextValue,
  };
}

function addQueryParam({
  clientFeedApiEndpoint,
  key,
  value,
}: {
  clientFeedApiEndpoint: string;
  key: string;
  value: string;
}): string {
  try {
    const clientFeedApiEndpointURL = new URL(clientFeedApiEndpoint);
    clientFeedApiEndpointURL.searchParams.set(key, value);
    return `${clientFeedApiEndpointURL.pathname}${clientFeedApiEndpointURL.search}`;
  } catch (error) {
    return clientFeedApiEndpoint;
  }
}
